/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import KcAdminClient from '@keycloak/keycloak-admin-client';
import GroupRepresentation from '@keycloak/keycloak-admin-client/lib/defs/groupRepresentation';
import { from, map, Observable } from 'rxjs';
import { AdministrationOrganisationsEinheit } from './organisations-einheit.model';

@Injectable({
  providedIn: 'root',
})
export class AdministrationOrganisationsEinheitRepository {
  private readonly kcAdminClient = inject(KcAdminClient);

  public getList(): Observable<AdministrationOrganisationsEinheit[]> {
    return this.getGroupsInKeycloak().pipe(
      map((groupReps: GroupRepresentation[]): AdministrationOrganisationsEinheit[] =>
        this.mapToOrganisationsEinheiten(groupReps),
      ),
    );
  }

  private getGroupsInKeycloak(): Observable<GroupRepresentation[]> {
    return from(this.kcAdminClient.groups.find({ briefRepresentation: false })).pipe(
      map((groups: GroupRepresentation[]): GroupRepresentation[] => groups || []),
    );
  }

  public create(organisationseinheit: AdministrationOrganisationsEinheit): Observable<AdministrationOrganisationsEinheit> {
    return this.createGroupInKeycloak(organisationseinheit).pipe(map(this.mapToOrganisationsEinheit));
  }

  private createGroupInKeycloak(organisationseinheit: AdministrationOrganisationsEinheit): Observable<GroupRepresentation> {
    return from(this.kcAdminClient.groups.create(organisationseinheit));
  }

  private mapToOrganisationsEinheiten(groupRepresentations: GroupRepresentation[]): AdministrationOrganisationsEinheit[] {
    return groupRepresentations
      .map((group: GroupRepresentation): AdministrationOrganisationsEinheit => this.mapToOrganisationsEinheit(group))
      .sort((toCompare: AdministrationOrganisationsEinheit, compareWith: AdministrationOrganisationsEinheit) =>
        toCompare.name.localeCompare(compareWith.name, 'de', { sensitivity: 'base' }),
      );
  }

  private mapToOrganisationsEinheit(group: GroupRepresentation): AdministrationOrganisationsEinheit {
    return {
      id: group.id,
      name: group.name,
      attributes: {
        organisationseinheitId: group.attributes['organisationseinheitId'],
        plz: group.attributes['plz'],
        ort: group.attributes['ort'],
        strasse: group.attributes['strasse'],
      },
    };
  }

  public delete(organisationseinheitId: string): Observable<void> {
    return from(this.kcAdminClient.groups.del({ id: organisationseinheitId }));
  }
}
